import {
  appointmentCursorSchema,
  dashboardAppointmentSchema,
} from './appointments-contracts'
import {
  normalizeAppointment,
  type AppointmentItem,
  type AppointmentView,
} from './appointments-model'

export const APPOINTMENT_PAGE_SIZE = 25
export const APPOINTMENT_QUERY_SIZE = APPOINTMENT_PAGE_SIZE + 1

export type AppointmentCursor = {
  scheduledAtMs: number
  appointmentId: string
}

export type AppointmentPage = {
  items: AppointmentItem[]
  cursor: AppointmentCursor | null
  hasMore: boolean
  invalidCount: number
  validCollection: boolean
}

function entries(value: unknown): Array<[string, unknown]> | null {
  if (value === null) return []
  if (typeof value !== 'object' || Array.isArray(value)) return null
  return Object.entries(value as Record<string, unknown>)
}

export function parseAppointmentPage(
  value: unknown,
  view: AppointmentView
): AppointmentPage {
  const collection = entries(value)
  if (collection === null) {
    return {
      items: [],
      cursor: null,
      hasMore: false,
      invalidCount: 0,
      validCollection: false,
    }
  }

  let invalidCount = 0
  const boundaryRows = collection
    .flatMap(([appointmentId, record]) => {
      const cursor = appointmentCursorSchema.safeParse(record)
      if (!appointmentId || !cursor.success) return []
      return [
        {
          appointmentId,
          scheduledAtMs: cursor.data.scheduledAtMs,
          record,
        },
      ]
    })
    .sort(
      (left, right) =>
        left.scheduledAtMs - right.scheduledAtMs ||
        left.appointmentId.localeCompare(right.appointmentId)
    )
  invalidCount += collection.length - boundaryRows.length

  const hasMore = boundaryRows.length > APPOINTMENT_PAGE_SIZE
  const pageRows = hasMore
    ? view === 'upcoming'
      ? boundaryRows.slice(0, APPOINTMENT_PAGE_SIZE)
      : boundaryRows.slice(1)
    : boundaryRows
  const cursorRow = view === 'upcoming' ? pageRows.at(-1) : pageRows[0]
  const items = pageRows.flatMap(({ appointmentId, record }) => {
    const parsed = dashboardAppointmentSchema.safeParse(record)
    if (
      !parsed.success ||
      parsed.data.appointmentId !== appointmentId ||
      parsed.data.temporalConfidence !== 'canonical' ||
      parsed.data.scheduledAtMs === null
    ) {
      invalidCount += 1
      return []
    }
    return [normalizeAppointment(parsed.data)]
  })

  items.sort((left, right) => {
    const timeOrder =
      (left.scheduledAtMs ?? 0) - (right.scheduledAtMs ?? 0) ||
      left.appointmentId.localeCompare(right.appointmentId)
    return view === 'upcoming' ? timeOrder : -timeOrder
  })
  return {
    items,
    cursor: cursorRow
      ? {
          scheduledAtMs: cursorRow.scheduledAtMs,
          appointmentId: cursorRow.appointmentId,
        }
      : null,
    hasMore,
    invalidCount,
    validCollection: true,
  }
}

export function parseReviewPage(value: unknown): AppointmentPage {
  const collection = entries(value)
  if (collection === null) {
    return {
      items: [],
      cursor: null,
      hasMore: false,
      invalidCount: 0,
      validCollection: false,
    }
  }
  let invalidCount = 0
  const items = collection.flatMap(([appointmentId, record]) => {
    const parsed = dashboardAppointmentSchema.safeParse(record)
    if (
      !parsed.success ||
      parsed.data.appointmentId !== appointmentId ||
      parsed.data.scheduledAtMs !== null
    ) {
      invalidCount += 1
      return []
    }
    return [normalizeAppointment(parsed.data)]
  })
  items.sort((left, right) =>
    right.appointmentId.localeCompare(left.appointmentId)
  )
  return {
    items: items.slice(0, APPOINTMENT_PAGE_SIZE),
    cursor: null,
    hasMore: collection.length > APPOINTMENT_PAGE_SIZE,
    invalidCount,
    validCollection: true,
  }
}

export function mergeAppointmentPages(
  pages: AppointmentPage[],
  view: AppointmentView
): AppointmentItem[] {
  const merged = new Map<string, AppointmentItem>()
  for (const page of pages) {
    for (const item of page.items) merged.set(item.appointmentId, item)
  }
  return [...merged.values()].sort((left, right) => {
    const order =
      (left.scheduledAtMs ?? 0) - (right.scheduledAtMs ?? 0) ||
      left.appointmentId.localeCompare(right.appointmentId)
    return view === 'upcoming' ? order : -order
  })
}
